import { NgComponentOutlet } from '@angular/common';
import { Component, OnInit, Type, ViewChild } from '@angular/core';
import { AppConfigService } from '@core/appconfig.service';
import { AuthenticationService } from '@core/authentication.service';
import { CheckUnsavedChanges } from '@core/guards/unsaved-changes.guard';
import { ZbPortalRedesignHelpers } from '@core/services/feature-flagged-features/zb-portal-redesign-helpers';
import { CompanyCode } from '@shared/enums/company-code';

/** Types of checks permitted to determine if the ZbRedesign component should be used. */
export type RedesignCheckType = 'default' | 'parentPortal2026';

/**
 * ViewHandlerComponent.
 *
 * Handles which component is returned based on Brand and Feature Flags
 *
 * Implementations can copy the below uncommenting and replacing the selector appropriately. Then copying the
 * componentMap below and setting the component.
 *
 * Remember to add the implementation to the correct module or routing won't work.
 */
@Component({
  template: `<ng-container *ngComponentOutlet="componentToUse | async"></ng-container>`,
  standalone: false
})
export abstract class ViewHandlerComponent implements OnInit, CheckUnsavedChanges {
  /**
   * The component to load in the outlet.
   *
   * This is a promise so that standalone components can be supported as well as traditional components.
   */
  componentToUse: Promise<Type<any>> | null;

  /**
   * The component map to use. Implementations should fill out the map with their own components.
   * Entries are optional, so you do not need to provide values for each.
   */
  componentMap = {
    [CompanyCode.HighlightsPortal]: { component: null },
    [CompanyCode.ZBPortal]: { component: null },
    ZbRedesign: { component: null },
  } as any;

  /**
   * Selects which feature-flag check decides whether to render the redesigned ZB component.
   * Subclasses override this to opt into a non-default check.
   */
  protected redesignCheckType: RedesignCheckType = 'default';

  constructor(
    public authService: AuthenticationService,
    private appConfig: AppConfigService,
    private zbPortalRedesignHelpers: ZbPortalRedesignHelpers
  ) { }

  ngOnInit(): void {
    this.initializeComponent();
  }

  /**
   * A default initializer for brand component handling.
   *
   * This can be overridden by the extending component to alter the logic. By default it chooses a component per
   * login brand.
   */
  initializeComponent(): void {
    let brandComponent = this.componentMap[this.appConfig.loginBrand]?.component;

    /**
     * If the configured redesign feature flag is active and there is a redesigned ZB component version,
     * then use the redesigned version for ZB Portal.
     */
    if (this.shouldUseZBRedesign() && this.componentMap.ZbRedesign?.component) {
      brandComponent = this.componentMap.ZbRedesign?.component;
    }

    if (brandComponent) {
      if (typeof brandComponent?.then === 'function') {
        this.componentToUse = brandComponent;
      }

      this.componentToUse = Promise.resolve(brandComponent);
    }
  }

  private shouldUseZBRedesign(): boolean {
    if (this.appConfig.loginBrand !== CompanyCode.ZBPortal) {
      return false;
    }
    switch (this.redesignCheckType) {
    case 'parentPortal2026':
      return this.zbPortalRedesignHelpers.isZBParentPortal2026Active();
    case 'default':
    default:
      return this.zbPortalRedesignHelpers.isZbPortal2024Active();
    }
  }

  @ViewChild(NgComponentOutlet, { static: false }) ngComponentOutlet: NgComponentOutlet;
  async confirmUnsavedChanges(): Promise<boolean> {
    if (this.ngComponentOutlet) {
      const componentInstance = this.ngComponentOutlet['_componentRef'].instance;
      if (componentInstance?.confirmUnsavedChanges) {
        return componentInstance.confirmUnsavedChanges();
      }
    }
    return true;
  }
}
